import Papa from 'papaparse';

import { TROVE_SKU_KEY } from './types';
import type { FieldCatalog, FieldDefinition, WizardState } from './types';
import type { CatalogTemplateItemRow } from './fetchCatalogItemsForTemplate';

const CUSTOM_FIELD_PREFIX = 'custom:';

function isCustomFieldKey(key: string): boolean {
  return key.startsWith(CUSTOM_FIELD_PREFIX);
}

/** Looks up a field's value by trying its source columns in order — first non-empty wins. */
function resolveFieldValue(item: CatalogTemplateItemRow, sourceColumns: string[]): string {
  for (const column of sourceColumns) {
    const value = item[column];
    if (value != null && value !== '') return String(value);
  }
  return '';
}

/**
 * Identifier + selected regular fields + any named custom fields, in that display order. Each
 * property is always a single CSV column headed by its human-readable name (e.g. "Brand"), even
 * when it's backed by more than one underlying DB column.
 */
export function getSelectedColumns(state: WizardState, catalog: FieldCatalog): FieldDefinition[] {
  const identifierCols = catalog.identifiers.filter(d => state.selectedIdentifiers.includes(d.key));
  const fieldCols = catalog.fields.filter(d => state.selectedFields.includes(d.key));
  const customCols: FieldDefinition[] = state.customFields
    .filter(c => c.key.trim())
    .map((c) => {
      const column = `${c.family}.${c.key.trim()}`;
      return { key: `${CUSTOM_FIELD_PREFIX}${c.id}`, label: column, csvColumn: column, sourceColumns: [column], required: false };
    });
  return [...identifierCols, ...fieldCols, ...customCols];
}

export interface TemplateRow {
  [csvColumn: string]: string;
}

/**
 * Builds template rows from fetched catalog items + wizard state. Identifier columns (and the
 * locked "Trove SKU" field, when present) are always pre-filled so rows match existing records
 * on re-upload; other selected columns are pre-filled with current values or left blank per
 * `state.valuesMode`. Custom field columns have no fetched value yet (the fetch only knows about
 * the partner's template catalog, not ad hoc keys the user types in), so they always render blank.
 */
export function buildTemplateRows(
  items: CatalogTemplateItemRow[],
  state: WizardState,
  catalog: FieldCatalog,
): TemplateRow[] {
  const columns = getSelectedColumns(state, catalog);
  const identifierKeys = new Set(catalog.identifiers.map(d => d.key));

  return items.map((item) => {
    const row: TemplateRow = {};
    for (const col of columns) {
      if (isCustomFieldKey(col.key)) {
        row[col.csvColumn] = '';
        continue;
      }
      const prefill = col.key === TROVE_SKU_KEY || identifierKeys.has(col.key) || state.valuesMode === 'prefill';
      row[col.csvColumn] = prefill ? resolveFieldValue(item, col.sourceColumns) : '';
    }
    return row;
  });
}

/**
 * Builds the CSV via Papa's object-input form (`fields` + `data`) rather than passing `rows`
 * directly with a `columns` config — when `rows` is empty (e.g. a "create new records" template,
 * which has no fetched items to populate rows from), Papa's array-input path ignores the
 * `columns` config and returns an empty string, dropping the header row entirely. The
 * object-input form always writes the header regardless of row count.
 */
export function generateTemplateCsv(items: CatalogTemplateItemRow[], state: WizardState, catalog: FieldCatalog): string {
  const fields = getSelectedColumns(state, catalog).map(c => c.csvColumn);
  const data = buildTemplateRows(items, state, catalog);
  return Papa.unparse({ fields, data });
}
